import axios from 'axios';

export const TRACKING_API_URL =
  process.env.REACT_APP_TRACKING_API_URL ||
  'https://api.boxdlogistics.com/api/v1/shipments/track-shipment/';

export const LAST_AWB_STORAGE_KEY = 'boxd:lastAwb';

// Call the tracking API. Throws with a user-friendly `.userMessage` on failure.
export async function trackShipment(awb, { signal } = {}) {
  const trimmed = String(awb || '').trim();
  if (!trimmed) {
    const err = new Error('Missing AWB');
    err.userMessage = 'Please enter a tracking / AWB number.';
    throw err;
  }

  try {
    const res = await axios.get(TRACKING_API_URL, {
      // Tracking API expects only `awb_number`.
      params: { awb_number: trimmed },
      signal,
      timeout: 15000,
      headers: { Accept: 'application/json' },
    });
    const normalized = normalizeTracking(res.data, trimmed);
    if (!normalized || (!normalized.status && !normalized.events.length)) {
      const err = new Error('Empty tracking payload');
      err.userMessage = 'Invalid tracking number or shipment not found.';
      throw err;
    }
    return normalized;
  } catch (err) {
    if (axios.isCancel(err) || err.name === 'CanceledError') throw err;
    if (err.userMessage) throw err;
    const wrapped = new Error(err.message || 'Tracking request failed');
    wrapped.userMessage = 'Invalid tracking number or shipment not found.';
    throw wrapped;
  }
}

// Convert a machine status code like "in_transit" into a readable label.
export function humanizeStatus(code) {
  if (!code) return '';
  return String(code)
    .replace(/[_-]+/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function pickFirstShipment(raw) {
  if (Array.isArray(raw)) return raw[0] || null;
  if (raw && Array.isArray(raw.data)) return raw.data[0] || null;
  if (raw && Array.isArray(raw.results)) return raw.results[0] || null;
  if (raw && typeof raw === 'object' && raw.data && typeof raw.data === 'object') {
    return raw.data;
  }
  return raw && typeof raw === 'object' ? raw : null;
}

// Normalize a variety of possible API response shapes into a predictable one.
export function normalizeTracking(raw, awbFallback) {
  const data = pickFirstShipment(raw);
  if (!data || typeof data !== 'object') return null;

  const awb =
    data.awb_number || data.awb || data.awbNumber || awbFallback || '';
  const statusCode =
    data.current_status ||
    data.status ||
    data.shipment_status ||
    data.latest_status ||
    '';
  const edd =
    data.edd ||
    data.expected_delivery_date ||
    data.estimated_delivery_date ||
    data.estimated_delivery ||
    data.expected_delivery ||
    null;
  const courier =
    data.courier_name ||
    data.courier ||
    data.carrier ||
    data.partner ||
    data.provider ||
    '';
  const orderId =
    data.order_number || data.order_id || data.orderId || data.reference_id || '';
  const route = data.route_code || data.route || '';

  const eventsRaw = Array.isArray(data.tracking_history)
    ? data.tracking_history
    : Array.isArray(data.events)
    ? data.events
    : Array.isArray(data.tracking_events)
    ? data.tracking_events
    : Array.isArray(data.timeline)
    ? data.timeline
    : Array.isArray(data.scans)
    ? data.scans
    : [];

  const events = eventsRaw
    .map((e) => {
      const evStatus =
        e.status || e.activity || e.event || e.scan_type || '';
      const description =
        (e.description && String(e.description).trim()) ||
        (e.remarks && String(e.remarks).trim()) ||
        (e.message && String(e.message).trim()) ||
        '';
      const timestamp =
        e.datetime ||
        e.timestamp ||
        e.date ||
        e.created_at ||
        e.scan_date ||
        e.event_time ||
        e.time ||
        null;
      const location = cleanLocation(
        e.location || e.scan_location || e.city || ''
      );
      const remark =
        e.remarks && e.remarks !== e.description
          ? String(e.remarks).trim()
          : '';
      return {
        statusCode: evStatus,
        title: description || humanizeStatus(evStatus) || 'Update',
        timestamp,
        location,
        remark,
      };
    })
    // Drop totally empty rows.
    .filter((e) => e.title || e.location || e.remark);

  // Sort newest first for display.
  events.sort((a, b) => {
    const ta = a.timestamp ? new Date(a.timestamp).getTime() : 0;
    const tb = b.timestamp ? new Date(b.timestamp).getTime() : 0;
    return tb - ta;
  });

  // Current location = most recent event with a location, fallback to empty.
  const currentLocation =
    events.find((e) => e.location)?.location || '';

  return {
    awb,
    orderId,
    status: humanizeStatus(statusCode),
    statusCode,
    edd,
    courier,
    route,
    currentLocation,
    events,
  };
}

// Drop trailing commas and "empty" placeholders like ", , ".
function cleanLocation(raw) {
  if (!raw) return '';
  const s = String(raw)
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);
  return s.join(', ');
}

// Map a status string/code into a UI bucket so we can pick colors/icons consistently.
export function classifyStatus(status) {
  const s = (status || '').toLowerCase();
  if (!s) return 'neutral';
  if (/deliver(ed)?\b/.test(s) && !/undeliver|out.?for/.test(s)) return 'delivered';
  if (/\brto\b|return/.test(s)) return 'rto';
  if (/cancel|fail|undeliver|\bndr\b|exception/.test(s)) return 'failed';
  if (/out.?for.?delivery|\bofd\b/.test(s)) return 'ofd';
  if (/pick/.test(s)) return 'pickup';
  if (/manifest|book|place|created|pending|schedul/.test(s)) return 'created';
  return 'transit';
}

export function formatEventTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return String(ts);
  return d.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatDate(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return String(ts);
  return d.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}
